import type { GeneratedNatalReport, InterpretationRulePack } from './interpretation.js';
import type { TopicId } from './rules.js';

export type TopicCoverageStatus = 'source_backed_triggered' | 'source_backed_no_trigger' | 'research_pending';

export interface TopicCoverageItem {
  topic: TopicId;
  status: TopicCoverageStatus;
  productionRuleCount: number;
  triggeredClaimCount: number;
  label: string;
  explanation: string;
}

export interface TopicCoverageViewModel {
  model: 'TOPIC-COVERAGE-VIEW-0.1.0';
  items: readonly TopicCoverageItem[];
  disclosure: string;
}

const STATUS_TEXT: Record<TopicCoverageStatus, Pick<TopicCoverageItem, 'label' | 'explanation'>> = {
  source_backed_triggered: {
    label: '已有可追溯判讀',
    explanation: '此主題已有來源核准規則，且目前命盤觸發至少一項 Claim。',
  },
  source_backed_no_trigger: {
    label: '已有規則，本命未觸發',
    explanation: '此主題已有來源核准規則，但目前命盤未符合已驗證條件，因此保持空白。',
  },
  research_pending: {
    label: '研究中',
    explanation: '此主題尚無 production 規則；系統不以 AI 或無來源文字補足。',
  },
};

export function buildTopicCoverageViewModel(
  report: GeneratedNatalReport,
  rulePack: InterpretationRulePack,
  topics: readonly TopicId[] = report.topics.map((item) => item.topic),
): TopicCoverageViewModel {
  const expectedVersion = `${rulePack.id}@${rulePack.version}`;
  if (report.versions.interpretationRulePack !== expectedVersion) {
    throw new Error(`Topic coverage rejected mismatched report/rule pack: ${report.versions.interpretationRulePack} != ${expectedVersion}`);
  }

  const items = [...new Set(topics)].map((topic): TopicCoverageItem => {
    const productionRuleCount = rulePack.rules.filter((rule) => rule.topic === topic).length;
    const triggeredClaimCount = report.topics.find((item) => item.topic === topic)?.claims.length ?? 0;
    const status: TopicCoverageStatus = productionRuleCount === 0
      ? 'research_pending'
      : triggeredClaimCount > 0
        ? 'source_backed_triggered'
        : 'source_backed_no_trigger';
    return Object.freeze({
      topic,
      status,
      productionRuleCount,
      triggeredClaimCount,
      ...STATUS_TEXT[status],
    });
  });

  return Object.freeze({
    model: 'TOPIC-COVERAGE-VIEW-0.1.0',
    items: Object.freeze(items),
    disclosure: '覆蓋狀態只表示目前 production 規則與命盤觸發情況，不代表準確率、信心值或預測機率。',
  });
}
